import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { t, type MessageKey } from '../../i18n/t';
import {
  addDays, addMonths, isoOutOfRange, isoParts, isoToDisplay, monthGrid, parseTypedDate, todayIso,
} from './dateInputLogic';
import './LopDateInput.css';

// Date field that ALWAYS shows dd/MM/yyyy (the native <input type="date"> follows the
// OS/browser locale). Contract matches the native input: value in/out is 'yyyy-MM-dd'
// or '' - callers' API payloads are unchanged. onChange is called with the string (not
// an event), and only with a VALID date or '' - while the typed text is invalid the
// text is kept, aria-invalid is set and nothing is emitted.

export interface LopDateInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string; // 'yyyy-MM-dd'
  max?: string;
  className?: string; // applied to the text <input> (so existing field CSS keeps working)
  wrapperClassName?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-testid'?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const monthName = (m: number) => t(`dateInput.months.m${m}` as MessageKey);
const dayShort = (i: number) => t(`dateInput.daysShort.d${i}` as MessageKey);
const dayLong = (i: number) => t(`dateInput.daysLong.d${i}` as MessageKey);

export function LopDateInput({
  value, onChange, id, name, disabled = false, required, min, max, className, wrapperClassName,
  'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'data-testid': testId, onKeyDown, onBlur,
}: LopDateInputProps) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  // External value change (parent reset / prefill): re-render the text unless the text
  // already denotes that value (so typing never gets overwritten mid-edit).
  useEffect(() => {
    const parsed = parseTypedDate(text);
    if (parsed !== (value || '')) {
      setText(isoToDisplay(value));
      setInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback(
    (iso: string) => {
      setText(isoToDisplay(iso));
      setInvalid(false);
      onChange(iso);
    },
    [onChange],
  );

  const handleText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);
    const parsed = parseTypedDate(raw);
    if (parsed === null || (parsed !== '' && isoOutOfRange(parsed, min, max))) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (parsed !== (value || '')) onChange(parsed);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const parsed = parseTypedDate(text);
    if (parsed && !invalid) setText(isoToDisplay(parsed)); // normalise "20.10.2026" -> "20/10/2026"
    onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'ArrowDown' && e.altKey) || (e.key === 'ArrowDown' && !text)) {
      e.preventDefault();
      if (!disabled) setOpen(true);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      return;
    }
    onKeyDown?.(e);
  };

  const closeAndFocus = () => {
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <span ref={wrapRef} className={`lop-date${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
        className={`lop-date__input${className ? ` ${className}` : ''}`}
        value={text}
        placeholder={t('dateInput.placeholder')}
        disabled={disabled}
        required={required}
        maxLength={10}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={invalid || undefined}
        title={invalid ? t('dateInput.invalid') : undefined}
        data-testid={testId}
        onChange={handleText}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        className="lop-date__btn"
        disabled={disabled}
        aria-label={t('dateInput.openCalendar')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarDays size={16} aria-hidden="true" />
      </button>
      {open && (
        <CalendarPopover
          anchor={wrapRef.current}
          value={invalid ? '' : value}
          min={min}
          max={max}
          onPick={(iso) => {
            emit(iso);
            closeAndFocus();
          }}
          onClose={closeAndFocus}
          onDismiss={() => setOpen(false)}
        />
      )}
    </span>
  );
}

interface PopoverProps {
  anchor: HTMLElement | null;
  value: string;
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
  onClose: () => void; // Escape: close + return focus
  onDismiss: () => void; // outside click: close only
}

function CalendarPopover({ anchor, value, min, max, onPick, onClose, onDismiss }: PopoverProps) {
  const today = todayIso();
  const initial = isoParts(value) ? value : today;
  const [focusIso, setFocusIso] = useState(initial);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const fp = isoParts(focusIso)!;

  useLayoutEffect(() => {
    const place = () => {
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const w = popRef.current?.offsetWidth ?? 260;
      const h = popRef.current?.offsetHeight ?? 290;
      let top = r.bottom + 4;
      if (top + h > window.innerHeight && r.top - h - 4 > 0) top = r.top - h - 4;
      const left = Math.max(4, Math.min(r.right - w, window.innerWidth - w - 4));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (popRef.current?.contains(n) || anchor?.contains(n)) return;
      onDismiss();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [anchor, onDismiss]);

  // Roving focus: move DOM focus to the active day whenever it changes.
  useEffect(() => {
    popRef.current?.querySelector<HTMLButtonElement>(`button[data-iso="${focusIso}"]`)?.focus();
  }, [focusIso, pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabledIso = (iso: string) => isoOutOfRange(iso, min, max);

  const onKey = (e: React.KeyboardEvent) => {
    // Calendar is RTL: the visual left is the LATER day.
    const move = (d: number) => {
      e.preventDefault();
      setFocusIso((cur) => addDays(cur, d));
    };
    switch (e.key) {
      case 'ArrowLeft': return move(1);
      case 'ArrowRight': return move(-1);
      case 'ArrowDown': return move(7);
      case 'ArrowUp': return move(-7);
      case 'PageDown': e.preventDefault(); return setFocusIso((c) => addMonths(c, 1));
      case 'PageUp': e.preventDefault(); return setFocusIso((c) => addMonths(c, -1));
      case 'Home': e.preventDefault(); return setFocusIso((c) => addDays(c, -(new Date(`${c}T00:00:00Z`).getUTCDay())));
      case 'End': e.preventDefault(); return setFocusIso((c) => addDays(c, 6 - new Date(`${c}T00:00:00Z`).getUTCDay()));
      case 'Escape': e.preventDefault(); e.stopPropagation(); return onClose();
      default:
    }
  };

  const cells = monthGrid(fp.y, fp.m);

  return createPortal(
    <div
      ref={popRef}
      className="lop-date__pop"
      role="dialog"
      aria-label={t('dateInput.calendar')}
      dir="rtl"
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
      onKeyDown={onKey}
      data-testid="lop-date-popover"
    >
      <div className="lop-date__head">
        <button type="button" className="lop-date__nav" aria-label={t('dateInput.prevMonth')}
          onClick={() => setFocusIso(addMonths(focusIso, -1))}>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
        <span className="lop-date__title" aria-live="polite">{monthName(fp.m)} {fp.y}</span>
        <button type="button" className="lop-date__nav" aria-label={t('dateInput.nextMonth')}
          onClick={() => setFocusIso(addMonths(focusIso, 1))}>
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="lop-date__grid" role="grid">
        {Array.from({ length: 7 }, (_, i) => (
          <span key={i} className="lop-date__dow" role="columnheader" aria-label={dayLong(i)}>{dayShort(i)}</span>
        ))}
        {cells.map((iso) => {
          const p = isoParts(iso)!;
          const dis = disabledIso(iso);
          const cls = [
            'lop-date__day',
            p.m !== fp.m ? 'lop-date__day--other' : '',
            iso === today ? 'lop-date__day--today' : '',
            iso === value ? 'lop-date__day--selected' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              className={cls}
              data-iso={iso}
              disabled={dis}
              tabIndex={iso === focusIso ? 0 : -1}
              aria-selected={iso === value}
              aria-current={iso === today ? 'date' : undefined}
              aria-label={`${p.d} ${monthName(p.m)} ${p.y}`}
              onClick={() => onPick(iso)}
            >
              {p.d}
            </button>
          );
        })}
      </div>
      <div className="lop-date__foot">
        <button type="button" className="lop-date__today" disabled={disabledIso(today)} onClick={() => onPick(today)}>
          {t('dateInput.today')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
